import { useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatCalendarDayLong } from "../../components/home/format";
import { topLevelVenues } from "../../components/home/home-context";
import { gamesDaysQuery } from "../../components/home/queries";
import { UnscheduledSection } from "../../components/home/unscheduled";
import { ProgressStrip } from "../../components/dashboard/progress-strip";
import { groupTotals } from "../../components/dashboard/status-groups";
import { MedalToggle } from "../../components/meets-list/medal-toggle";
import { MeetsFilters } from "../../components/meets-list/meets-filters";
import { MeetsTable } from "../../components/meets-list/meets-table";
import { gamesVenueOptionsQuery } from "../../components/meets-list/queries";
import {
	EMPTY_DRAFT,
	type MeetsDraft,
	type MeetsSearch,
	activeFilterCount,
	draftFromSearch,
	meetsFilters,
	meetsSearch,
	searchFromDraft,
} from "../../components/meets-list/search";
import { FilterPanel } from "../../components/page/filter-panel";
import { PageHeader } from "../../components/page/page-header";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";
import { scheduleQuery, statusCountsQuery } from "../../lib/meet";

/** Rows per keyset page: "Next 50". Keyset pages have no total, so no page numbers. */
const PAGE = 50;

/**
 * Meets › All sports — every meet of the current games in start order, with
 * the filters in the URL (`?sport&discipline&venue&day&status&medal`). The
 * old OVR's layout: a fold-out "Filters" card, the progress card (how far the
 * meets that match have got, a chip per status that narrows the list), then
 * the table card headed by "Medal events only" and the count. The status is
 * a dashboard group (`status-groups.ts`), so a chip on the dashboard and this
 * list always agree on what "Live" means.
 */
export const Route = createFileRoute("/_app/meets/")({
	validateSearch: meetsSearch,
	component: MeetsListPage,
});

function MeetsListPage() {
	const { t } = useTranslation("meetsList");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/meets/" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;

	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });
	// Only the venues this games plays in (R3b), each hall without its courts — a hall's filter includes them.
	const venueOptions = useQuery({ ...gamesVenueOptionsQuery(gamesId ?? ""), enabled: gamesId !== null });
	const venues = useMemo(() => topLevelVenues(venueOptions.data ?? []), [venueOptions.data]);
	const gamesDays = useQuery(gamesDaysQuery(current.games));

	const go = useCallback((next: MeetsSearch) => void navigate({ search: next }), [navigate]);
	const applied = useMemo(() => draftFromSearch(search), [search]);
	// "Reset" clears the Filters card only: the medal toggle at the head of the table stays as it is.
	const medalOn = search.medal === true;
	const empty = useMemo<MeetsDraft>(() => ({ ...EMPTY_DRAFT, medal: medalOn }), [medalOn]);
	const f = useFilterDraft<MeetsDraft>(applied, (d) => go(searchFromDraft(d)), empty);

	// The sidebar switched games: sports, disciplines and days belong to the old one — keep only the status.
	const lastGames = useRef(gamesId);
	useEffect(() => {
		const previous = lastGames.current;
		lastGames.current = gamesId;
		if (previous !== null && gamesId !== null && previous !== gamesId) {
			void navigate({ search: { status: search.status }, replace: true });
		}
	}, [gamesId]);

	const filters = gamesId ? meetsFilters(gamesId, search) : null;
	const list = useInfiniteQuery({
		...scheduleQuery(filters ?? {}, PAGE),
		enabled: filters !== null,
		placeholderData: keepPreviousData,
	});
	const rows = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
	const filtered = activeFilterCount(search) > 0;
	// The progress card counts what the filters match, whatever the status: its chips pick the status.
	const counts = useQuery({
		...statusCountsQuery(gamesId ? meetsFilters(gamesId, { ...search, status: undefined }) : {}),
		enabled: gamesId !== null,
		placeholderData: keepPreviousData,
	});
	const totals = useMemo(() => (counts.data ? groupTotals(counts.data) : null), [counts.data]);

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	const count = formatInteger(rows.length);
	const shown = search.day
		? t("list.showingDay", { count: rows.length, n: count, day: formatCalendarDayLong(search.day) })
		: t("list.showing", { count: rows.length, n: count });

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />

			<div className="flex flex-col gap-[1rem]">
				<FilterPanel activeCount={activeFilterCount(search) - (medalOn ? 1 : 0)}>
					<MeetsFilters
						draft={f.draft}
						set={f.set}
						onApply={f.apply}
						onReset={f.reset}
						activeCount={activeFilterCount(search) - (medalOn ? 1 : 0)}
						sports={sports.data ?? []}
						venues={venues}
						days={gamesDays.data?.days ?? []}
					/>
				</FilterPanel>

				{totals && totals.total > 0 && <ProgressStrip totals={totals} linkSearch={(status) => ({ ...search, status })} current={search.status} />}

				<div className="flex flex-col gap-[0.75rem]">
					<MeetsTable
						rows={rows}
						loading={list.isPending || (list.isFetching && !list.isFetchingNextPage && list.isPlaceholderData)}
						error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
						empty={
							filtered
								? {
										title: t("empty.filteredTitle"),
										description: t("empty.filteredBody"),
										// Everything, the medal toggle included: "Reset" in the Filters card keeps the toggle, this would then do nothing.
										action: (
											<Button variant="outline" size="sm" onClick={() => go({})}>
												{t("empty.reset")}
											</Button>
										),
									}
								: { title: t("empty.noneTitle"), description: t("empty.noneBody") }
						}
						clockOnly={search.day !== undefined}
						toolbar={
							<span className="flex flex-wrap items-center gap-x-[0.75rem] gap-y-[0.375rem]">
								<MedalToggle on={medalOn} onToggle={() => go({ ...search, medal: medalOn ? undefined : true })} />
								{rows.length > 0 && <span className="text-[0.8125rem] text-muted">{shown}</span>}
							</span>
						}
					/>
					{rows.length > 0 && (
						<div className="flex flex-wrap items-center justify-center gap-[0.75rem]">
							{list.hasNextPage ? (
								<Button variant="outline" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
									{t("list.more", { n: formatInteger(PAGE) })}
								</Button>
							) : (
								<p className="text-[0.875rem] text-muted">{t("list.allLoaded")}</p>
							)}
						</div>
					)}
				</div>

				{/* GET /meets lists timed meets only; without a day, the untimed ones the counts include are here. */}
				{filters && !search.day && <UnscheduledSection key={JSON.stringify(filters)} filters={{ ...filters, games_id: current.games.games_id }} />}
			</div>
		</div>
	);
}
